import { useAuth } from "@/contexts/AuthContext";
import {
  CreateOrderInitialValues,
  CreateOrderWizard,
  mapCreateOrderToBackendFormat,
} from "./helpers/createOrderHelpers";
import { Group, Text } from "@mantine/core";
import { MutableRefObject, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postCreateOrder, useGetCreateOrderValues } from "@/services/Orders";
import { useGetCountries } from "@/services/RegisterCompany";
import { CreateOrder } from "@/types";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import OrderTypeEnum, { OrderTypeEnumString } from "@/constants/enums/OrderTypeEnum";
import Stepper from "@/components/molecules/Stepper";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import styles from "./styles.module.css";
import Product from "./Steps/Product";
import Delivery from "./Steps/Delivery";
import Image from "./Steps/Image";
import CTA from "@/components/atoms/CTA";
import OrderCta from "@/components/atoms/OrderCTA";
import useIndexedRef from "@/hooks/useIndexedRef";

type CreateOrderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
};

export const CreateOrderModal = ({ isOpen, onClose }: CreateOrderModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentCompany, user } = useAuth();
  const formSections = useRef<(HTMLFormElement | null)[]>([]);

  const [createOrderWizardData, setCreateOrderWizardData] = useState<CreateOrderWizard>(CreateOrderInitialValues);
  const [steps, setSteps] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState<boolean>(false);

  const { data: unitData } = useGetCreateOrderValues();
  const { data: countries } = useGetCountries(user?.language?.id ?? 1, {
    enabled: !!user?.language?.id,
  });

  const { mutateAsync: mutateCreateOrder } = useMutation({
    mutationFn: async (data: CreateOrder) => {
      await postCreateOrder(data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getOrdersForCompany", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:orders.orderCreated"))
      );
      onCloseCreateOrderModal();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.orderCreatedError"))
      );
    },
  });

  const openCreateOrderModal = (orderType: OrderTypeEnum, numberOfSteps: number) => {
    setCreateOrderWizardData((prev) => ({ ...prev, orderType }));
    setSteps(numberOfSteps);
    onClose();
    setIsOrderModalOpen(true);
  };

  const previousStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const onChangeWizardData = (changed: Partial<CreateOrderWizard>) => {
    setCreateOrderWizardData((prev) => ({ ...prev, ...changed }));
  };

  const validateCurrentStep = () => {
    const form = formSections.current[currentStep];
    if (form) {
      form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    }
  };

  const onFormSectionValidationSuccess = async (data?: Partial<CreateOrderWizard>) => {
    if (currentStep === steps - 1) {
      await createOrder(data);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const createOrder = async (overrideData?: Partial<CreateOrderWizard>) => {
    if (!unitData) return;
    const merged = overrideData ? { ...createOrderWizardData, ...overrideData } : createOrderWizardData;
    const finalData = await mapCreateOrderToBackendFormat(
      merged,
      currentCompany?.companyId as number,
      user?.id as number,
      unitData?.currencies,
      countries ?? []
    );

    if (!finalData) {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.orderCreatedError"))
      );
      return;
    }

    await mutateCreateOrder(finalData);
  };

  const onCloseCreateOrderModal = () => {
    setCurrentStep(0);
    setCreateOrderWizardData(CreateOrderInitialValues);
    setIsOrderModalOpen(false);
  };

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={onClose}
        showCloseButton={true}
        size="lg"
        title={<ModalHeader title={t("orders:purchaseOrSell")} />}
      >
        <Group justify="center" p="xl">
          <OrderCta
            id="create-sales-order"
            onClick={() => openCreateOrderModal(OrderTypeEnum.SELL, 3)}
            orderType={OrderTypeEnumString.SELL}
          />
          <OrderCta
            id="create-buy-order"
            onClick={() => openCreateOrderModal(OrderTypeEnum.BUY, 2)}
            orderType={OrderTypeEnumString.BUY}
          />
        </Group>
      </Modal>

      <Modal
        opened={isOrderModalOpen}
        onClose={onCloseCreateOrderModal}
        closeOnClickOutside={false}
        size="xl"
        yOffset={"15vh"}
        centered={false}
        title={<ModalHeader title={"Create Order*"} />}
      >
        {createOrderWizardData.orderType === OrderTypeEnum.BUY ? (
          <BuyOrderSteps
            currentStep={currentStep}
            onFormSectionValidationSuccess={onFormSectionValidationSuccess}
            onChangeWizardData={onChangeWizardData}
            createOrderWizardData={createOrderWizardData}
            formSections={formSections}
          />
        ) : (
          <SalesOrderSteps
            currentStep={currentStep}
            onFormSectionValidationSuccess={onFormSectionValidationSuccess}
            onChangeWizardData={onChangeWizardData}
            createOrderWizardData={createOrderWizardData}
            formSections={formSections}
          />
        )}

        <ModalCTAs>
          <CTA id="cancel-create-contract" intent="secondary" onClick={onCloseCreateOrderModal}>
            {t("actions:cancel")}
          </CTA>

          {currentStep > 0 && (
            <CTA id="back-create-sales-order" intent="secondary" onClick={previousStep}>
              {t("actions:back")}
            </CTA>
          )}

          <CTA id="next-create-sales-order" intent="primary" onClick={validateCurrentStep}>
            {currentStep < steps - 1 ? t("actions:next") : t("actions:create")}
          </CTA>
        </ModalCTAs>
      </Modal>
    </>
  );
};

const ModalHeader = ({ title }: { title: string }) => {
  return (
    <Group>
      <SVGAsset name="contract" />
      <Text size="xl">{title.toUpperCase()}</Text>
    </Group>
  );
};

interface StepProps {
  currentStep: number;
  onFormSectionValidationSuccess: (data?: Partial<CreateOrderWizard>) => void;
  onChangeWizardData: (changed: Partial<CreateOrderWizard>) => void;
  createOrderWizardData: CreateOrderWizard;
  formSections: MutableRefObject<(HTMLFormElement | null)[]>;
}

const BuyOrderSteps = ({
  currentStep,
  onFormSectionValidationSuccess,
  onChangeWizardData,
  createOrderWizardData,
  formSections,
}: StepProps) => {
  const { t } = useTranslation();

  const setForm0 = useIndexedRef<HTMLFormElement>(formSections, 0);
  const setForm1 = useIndexedRef<HTMLFormElement>(formSections, 1);
  return (
    <Stepper
      active={currentStep}
      classNames={{
        stepIcon: styles.stepIcon,
        step: styles.step,
        stepLabel: styles.stepLabel,
        separator: styles.separator,
        steps: styles.stepperHeader,
        content: styles.stepperContent,
      }}
      size="sm"
      completedIcon={<SVGAsset name="step-complete" />}
    >
      <Stepper.Step label={t("common:type")}>
        <Product
          ref={setForm0}
          onValidationSuccess={onFormSectionValidationSuccess}
          onChangeWizardData={onChangeWizardData}
          wizardData={createOrderWizardData}
        />
      </Stepper.Step>
      <Stepper.Step label={t("orders:locationAndDelivery")}>
        <Delivery
          ref={setForm1}
          onValidationSuccess={onFormSectionValidationSuccess}
          onChangeWizardData={onChangeWizardData}
          wizardData={createOrderWizardData}
        />
      </Stepper.Step>
    </Stepper>
  );
};

const SalesOrderSteps = ({
  currentStep,
  onFormSectionValidationSuccess,
  onChangeWizardData,
  createOrderWizardData,
  formSections,
}: StepProps) => {
  const { t } = useTranslation();

  const setForm0 = useIndexedRef<HTMLFormElement>(formSections, 0);
  const setForm1 = useIndexedRef<HTMLFormElement>(formSections, 1);
  const setForm2 = useIndexedRef<HTMLFormElement>(formSections, 2);
  return (
    <Stepper
      active={currentStep}
      classNames={{
        stepIcon: styles.stepIcon,
        step: styles.step,
        separator: styles.separator,
        stepLabel: styles.stepLabel,
        steps: styles.stepperHeader,
        content: styles.stepperContent,
      }}
      size="sm"
      completedIcon={<SVGAsset name="step-complete" />}
    >
      <Stepper.Step label={t("common:type")}>
        <Product
          ref={setForm0}
          onValidationSuccess={onFormSectionValidationSuccess}
          onChangeWizardData={onChangeWizardData}
          wizardData={createOrderWizardData}
        />
      </Stepper.Step>
      <Stepper.Step label={t("common:image")}>
        <Image
          ref={setForm1}
          onValidationSuccess={onFormSectionValidationSuccess}
          onChangeWizardData={onChangeWizardData}
          wizardData={createOrderWizardData}
        />
      </Stepper.Step>
      <Stepper.Step label={t("orders:locationAndDelivery")}>
        <Delivery
          ref={setForm2}
          onValidationSuccess={onFormSectionValidationSuccess}
          onChangeWizardData={onChangeWizardData}
          wizardData={createOrderWizardData}
        />
      </Stepper.Step>
      {/* <Stepper.Step label={t("product:packaging")}>
        <Packaging
          ref={(el: HTMLFormElement | null) => (formSections.current[3] = el)}
          onValidationSuccess={onFormSectionValidationSuccess}
          onChangeWizardData={onChangeCreateOrderValue}
          wizardData={createOrderValues}
        />
      </Stepper.Step> */}
    </Stepper>
  );
};
